import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { receivableDetailApi, FG03, DetailCreate } from '../../../services/receivableDetailApi';

export type Mode = 'browse' | 'add' | 'edit';

const DOC_TYPE_LABELS: Record<string, string> = {
  G: '大貨',
  S: '樣品',
  I: '庫存',
  E: '調整單',
};

export function validateReceivableDetailClient(input: {
  mode: Mode; companyCode: string; shipNo: string; currency: string; docType: string;
}): string[] {
  const errors: string[] = [];
  if (input.mode === 'add' && !input.companyCode.trim()) errors.push('fg03_01');
  if (input.mode === 'add' && !input.shipNo.trim()) errors.push('fg03_02');
  if (!input.currency.trim()) errors.push('fg03_16');
  if (!input.docType.trim()) errors.push('fg03_21');
  return errors;
}

export function useReceivableDetailForm(canView = true) {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<FG03[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [loading, setLoading] = useState(false);

  // Form fields — master
  const [companyCode, setCompanyCode] = useState('');
  const [shipNo, setShipNo] = useState('');
  const [receivableAmt, setReceivableAmt] = useState(0);
  const [paymentAmt, setPaymentAmt] = useState(0);
  const [returnAmt, setReturnAmt] = useState(0);
  const [discountAmt, setDiscountAmt] = useState(0);
  const [complaintAmt, setComplaintAmt] = useState(0);
  const [currency, setCurrency] = useState('');
  const [docType, setDocType] = useState('G');
  const [adjustAmt, setAdjustAmt] = useState(0);
  const [canClaim, setCanClaim] = useState(false);

  // Form fields — details (child table)
  const [details, setDetails] = useState<DetailCreate[]>([]);

  const current = currentIdx >= 0 && currentIdx < records.length ? records[currentIdx] : null;
  const isEditing = mode !== 'browse';

  // Computed: doc type label
  const docTypeLabel = DOC_TYPE_LABELS[docType] || '';

  // Computed: outstanding balance (BR-S02-F03)
  const outstandingBalance = receivableAmt - paymentAmt - returnAmt - discountAmt - complaintAmt + adjustAmt;

  // Load all records
  const loadRecords = useCallback(async () => {
    if (!canView) return [];
    setLoading(true);
    try {
      const res = await receivableDetailApi.list(1, 1000);
      setRecords(res.data || []);
      return res.data || [];
    } catch {
      message.error('載入失敗');
      return [];
    } finally {
      setLoading(false);
    }
  }, [canView]);

  // Sync form fields from current record
  const syncFromRecord = useCallback((rec: FG03) => {
    setCompanyCode(rec.fg03_01);
    setShipNo(rec.fg03_02);
    setReceivableAmt(rec.fg03_11);
    setPaymentAmt(rec.fg03_12);
    setReturnAmt(rec.fg03_13);
    setDiscountAmt(rec.fg03_14);
    setComplaintAmt(rec.fg03_15);
    setCurrency(rec.fg03_16);
    setDocType(rec.fg03_21);
    setAdjustAmt(rec.fg03_41);
    setCanClaim(rec.fg03_42);
    setDetails((rec.details || []).map(d => ({
      fg031_03: d.fg031_03,
      fg031_11: d.fg031_11,
      fg031_12: d.fg031_12,
    })));
  }, []);

  useEffect(() => {
    if (current && mode === 'browse') {
      syncFromRecord(current);
    }
  }, [current, mode, syncFromRecord]);

  // Initial load
  useEffect(() => {
    loadRecords().then(data => {
      if (data.length > 0) setCurrentIdx(0);
    });
  }, [loadRecords]);

  // Navigation
  const navFirst = () => { if (!isEditing && records.length > 0) setCurrentIdx(0); };
  const navPrev = () => { if (!isEditing && currentIdx > 0) setCurrentIdx(currentIdx - 1); };
  const navNext = () => { if (!isEditing && currentIdx < records.length - 1) setCurrentIdx(currentIdx + 1); };
  const navLast = () => { if (!isEditing && records.length > 0) setCurrentIdx(records.length - 1); };

  // Start add mode (BR-F05 defaults: docType=G)
  const startAdd = () => {
    setMode('add');
    setCompanyCode('');
    setShipNo('');
    setReceivableAmt(0);
    setPaymentAmt(0);
    setReturnAmt(0);
    setDiscountAmt(0);
    setComplaintAmt(0);
    setCurrency('');
    setDocType('G');
    setAdjustAmt(0);
    setCanClaim(false);
    setDetails([]);
  };

  // Start edit mode
  const startEdit = () => {
    if (!current) return;
    setMode('edit');
    syncFromRecord(current);
  };

  // Cancel (restore)
  const cancel = () => {
    setMode('browse');
    if (current) {
      syncFromRecord(current);
    }
  };

  // Save
  const save = async () => {
    const clientErrors = validateReceivableDetailClient({ mode, companyCode, shipNo, currency, docType });
    if (clientErrors.length > 0) {
      message.error(`資料驗證失敗：${clientErrors.join(', ')}`);
      return;
    }
    try {
      if (mode === 'add') {
        await receivableDetailApi.create({
          fg03_01: companyCode, fg03_02: shipNo,
          fg03_11: receivableAmt, fg03_12: paymentAmt,
          fg03_13: returnAmt, fg03_14: discountAmt,
          fg03_15: complaintAmt, fg03_16: currency,
          fg03_21: docType, fg03_41: adjustAmt,
          fg03_42: canClaim, details,
        });
        message.success('新增成功');
      } else {
        await receivableDetailApi.update(current!.fg03_01, current!.fg03_02, {
          fg03_11: receivableAmt, fg03_12: paymentAmt,
          fg03_13: returnAmt, fg03_14: discountAmt,
          fg03_15: complaintAmt, fg03_16: currency,
          fg03_21: docType, fg03_41: adjustAmt,
          fg03_42: canClaim, details,
        });
        message.success('修改成功');
      }
      const data = await loadRecords();
      const targetCC = mode === 'add' ? companyCode : current!.fg03_01;
      const targetSN = mode === 'add' ? shipNo : current!.fg03_02;
      const idx = data.findIndex(r => r.fg03_01 === targetCC && r.fg03_02 === targetSN);
      setCurrentIdx(idx >= 0 ? idx : 0);
      setMode('browse');
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) {
        vErrs.forEach((e: any) => message.error(e.message));
      } else {
        message.error(err?.response?.data?.error || '儲存失敗');
      }
    }
  };

  // Delete (BR-F10)
  const doDelete = async () => {
    if (!current) return;
    try {
      await receivableDetailApi.delete(current.fg03_01, current.fg03_02);
      message.success('刪除成功');
      const data = await loadRecords();
      if (data.length > 0) {
        setCurrentIdx(Math.min(currentIdx, data.length - 1));
      } else {
        setCurrentIdx(-1);
      }
    } catch {
      message.error('刪除失敗');
    }
  };

  // Quick search (BR-F11)
  const quickSearch = (searchCompany: string, searchShipNo: string) => {
    if (isEditing) return;
    const key = searchCompany + searchShipNo;
    const idx = records.findIndex(r => (r.fg03_01 + r.fg03_02) >= key);
    if (idx >= 0) setCurrentIdx(idx);
  };

  return {
    mode, isEditing, loading,
    records, current, currentIdx,
    companyCode, setCompanyCode,
    shipNo, setShipNo,
    receivableAmt, setReceivableAmt,
    paymentAmt, setPaymentAmt,
    returnAmt, setReturnAmt,
    discountAmt, setDiscountAmt,
    complaintAmt, setComplaintAmt,
    currency, setCurrency,
    docType, setDocType, docTypeLabel,
    adjustAmt, setAdjustAmt,
    canClaim, setCanClaim,
    details, setDetails,
    outstandingBalance,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save,
    doDelete, quickSearch,
    loadRecords,
  };
}
